import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useParams, useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';
import { useToast } from '../contexts/ToastContext';
import { useSport, sportTypes } from '../contexts/SportContext';
import { useNotification } from '../contexts/NotificationContext';
import EventCreationModal from '../components/EventCreationModal';
import MyEventsModal from '../components/MyEventsModal';
import EventDetails from '../components/EventDetails';
import ChangeVibeModal from '../components/ChangeVibeModal';
import NotificationPrompt from '../components/NotificationPrompt';
import WelcomeModal from '../components/WelcomeModal';
import LanguageModal from '../components/LanguageModal';
import { useLocalWeather } from '../hooks/useLocalWeather';
import { useEventbriteNearby } from '../hooks/useEventbriteNearby';
import { useModalState } from '../hooks/useModalState';
import { useViewMode } from '../hooks/useViewMode';
import { motion, AnimatePresence } from 'framer-motion';
import './MapPage.css';
import TopBarButton from '../components/TopBarButton';
import Marquee from '../components/Marquee';
import { adItems } from '../config/ads.js'; // configuration still supplies images and preferred text-order

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png', iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png', shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png' });

const FlyToController = ({ event }) => {
    const map = useMap();
    useEffect(() => {
        if (event) {
            map.flyTo([event.latitude, event.longitude], 15, { animate: true, duration: 1.5 });
            const timerId = setTimeout(() => {
                map.eachLayer((layer) => {
                    if (layer.options.eventId === event.id) {
                        const markerElement = layer.getElement();
                        if (markerElement) {
                            markerElement.classList.add('pulse-animation');
                            setTimeout(() => markerElement.classList.remove('pulse-animation'), 1200);
                        }
                    }
                });
            }, 1500);
            return () => clearTimeout(timerId);
        }
    }, [event, map]);
    return null;
};

const MapController = ({ setCreateEventCoords, eventsToDisplay, setSelectedEvent, user, onViewportChange }) => {
    const map = useMap();
    const timerRef = useRef(null);
    const hasLocatedRef = useRef(false);
    const locateButtonRef = useRef(null);
    const createHereButtonRef = useRef(null);

    useMapEvents({
        click(e) { setCreateEventCoords(e.latlng); },
        moveend() {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => onViewportChange(map.getBounds()), 500);
        }
    });

    useEffect(() => {
        if (!hasLocatedRef.current) {
            map.locate({ setView: true, maxZoom: 14 });
            hasLocatedRef.current = true;
        }
        if (locateButtonRef.current) {
            L.DomEvent.disableClickPropagation(locateButtonRef.current);
        }
        if (createHereButtonRef.current) {
            L.DomEvent.disableClickPropagation(createHereButtonRef.current);
        }
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [map]);

    const locateUser = (e) => {
        e.preventDefault();
        e.stopPropagation();
        map.locate({ setView: true, maxZoom: 14 });
    };

    const createEventHere = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Event an aktueller GPS-Position erstellen
        map.locate();
        map.once('locationfound', (locationEvent) => {
            setCreateEventCoords(locationEvent.latlng);
        });
    };
    
    const getMarkerClassName = (event) => {
        if (!user || !event.participants) return '';
        const myParticipation = event.participants.find(p => p.user_id === user.id);
        if (event.user_id === user.id) return 'marker-owned';
        if (myParticipation?.status === 'accepted') return 'marker-joined';
        if (myParticipation?.status === 'pending') return 'marker-pending';
        return '';
    };
    
    return (
        <>
            <button type="button" ref={createHereButtonRef} title="Create Event Here" onClick={createEventHere} className="map-fab create">
                +
            </button>
            <button type="button" ref={locateButtonRef} title="My Location" onClick={locateUser} className="map-fab locate">
                &#9678;
            </button>
            <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={60}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
            >
                {eventsToDisplay.map(event => (
                    <Marker key={event.id} eventId={event.id} position={[event.latitude, event.longitude]} icon={L.divIcon({
                        html: `${sportTypes[event.sport_type]?.icon || '📍'}`,
                        className: `emoji-marker ${getMarkerClassName(event)}`,
                        iconSize: [40, 40],
                        iconAnchor: [20, 40]
                    })}
                    eventHandlers={{ click: () => setSelectedEvent(event) }} />
                ))}
            </MarkerClusterGroup>
        </>
    );
};

const StatusBarContent = () => {
    const { t, i18n } = useTranslation();

    // build list of items for marquee based on config and translation bundle
    const bundle = i18n.getResourceBundle(i18n.language, 'translation') || {};
    const allKeys = Object.keys(bundle).filter(k => k.startsWith('ad_marquee_'));
    const seenKeys = new Set();

    const items = adItems.map((it) => {
        if (it.type === 'image') {
            return { type: 'image', src: it.src };
        }
        // marquee text entry (type 'marquee' in config)
        seenKeys.add(it.textKey);
        return { type: 'text', textKey: it.textKey, defaultText: it.default };
    });

    // include any translation keys that weren't mentioned in config
    allKeys.forEach((key) => {
        if (!seenKeys.has(key)) {
            items.push({ type: 'text', textKey: key });
        }
    });

    if (items.length === 0) {
        items.push({ type: 'text', defaultText: 'Welcome to your GONKLE!' });
    }

    return <Marquee items={items} />;
};

const MapPage = () => {
    const { t, i18n } = useTranslation();
    const { logout, user, updateUser, api } = useAuth();
    const { events, error: eventError, fetchEventsByBounds, setMapBounds, pendingRequestCount } = useEvents();
    const { showError } = useToast();
    const { notification } = useNotification();
    const hasVisibleNotification = Boolean(notification?.text && notification.text.trim().length > 0);
    const { weather, requestLocation } = useLocalWeather(
        process.env.REACT_APP_OPENWEATHER_KEY
    );
    const { events: suggestedGonkles, isLoading: loadingSuggested, fetchNearbyEvents, error: nearbyError, clearCache: clearNearbyCache } = useEventbriteNearby(api);

    const [createEventCoords, setCreateEventCoords] = useState(null);
    const [flyToEvent, setFlyToEvent] = useState(null);
    const [showWelcome, setShowWelcome] = useState(false);
    const [showSaveAccessOverlay, setShowSaveAccessOverlay] = useState(false);

    const {
        showMyEvents, setShowMyEvents,
        selectedEvent, setSelectedEvent,
        showChangeVibe, setShowChangeVibe,
        showLanguageModal, setShowLanguageModal,
        showSuggestedGonkles, setShowSuggestedGonkles,
        handleOpenChangeVibe, handleCloseSelectedEvent
    } = useModalState();
    const { viewMode, setViewMode, cycleViewMode, viewModeIcon, viewModeText } = useViewMode(user);

    const { id: eventIdFromUrl } = useParams();
    const navigate = useNavigate();
    const defaultPosition = [51.16, 10.45];

    const filteredEvents = useMemo(() => {
        let filtered = events;
        
        // Filter 1: Abgelaufene Events entfernen (nur zukünftige zeigen)
        const now = new Date();
        filtered = filtered.filter(event => {
            const eventTime = new Date(event.event_date);
            return eventTime > now; // Nur Events in der Zukunft
        });
        
        // Filter 2: Nach ViewMode
        if (viewMode === 'my' && user?.favorite_sport) {
            filtered = filtered.filter(event => {
                return event.sport_type === user.favorite_sport;
            });
        } else if (viewMode === 'today') {
            // WICHTIG: Nutze lokale Zeit für Vergleich (User sieht lokale Uhrzeit)
            const localToday = formatDate(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' });
                filtered = filtered.filter(event => {
                const eventDate = formatDate(new Date(event.event_date), { year: 'numeric', month: '2-digit', day: '2-digit' });
                return eventDate === localToday;
            });
        }

        return filtered;
    }, [events, viewMode, user?.favorite_sport]);
    
    // Check for first visit
    useEffect(() => {
        const hasVisited = localStorage.getItem('gonkle_has_visited');
        if (!hasVisited) {
            setShowWelcome(true);
        }
    }, []);

    useEffect(() => {
        requestLocation();
    }, [requestLocation]);

  // demo: open language chooser automatically if env flag set
  useEffect(() => {
    if (process.env.REACT_APP_DEMO_LANGUAGE_MODAL === 'true') {
      setShowLanguageModal(true);
    }
  }, [setShowLanguageModal]);
    useEffect(() => {
        if (weather?.location && user?.favorite_sport) {
            fetchNearbyEvents(
                weather.location.lat,
                weather.location.lon,
                10, // 10km radius
                [user.favorite_sport]
            );
        }
    }, [weather?.location, user?.favorite_sport, fetchNearbyEvents]);

    // show any error from nearby hook as a toast
    useEffect(() => {
        if (nearbyError) {
            showError(nearbyError);
        }
    }, [nearbyError, showError]);

    // if location jumps significantly (more than 1km), flush cache so we fetch fresh
    useEffect(() => {
        if (weather?.location) {
            // on first load there is no cache key or previous coords
            clearNearbyCache();
        }
        // we rely on hook's internal key but external clear ensures updates when map reopens
    }, [weather?.location, clearNearbyCache]);

    // Auto-dismiss Nearby modal after 8 seconds
    useEffect(() => {
        if (showSuggestedGonkles && suggestedGonkles.length > 0) {
            const timer = setTimeout(() => {
                setShowSuggestedGonkles(false);
            }, 8000);
            
            return () => clearTimeout(timer);
        }
    }, [showSuggestedGonkles, suggestedGonkles.length]);

    const handleWelcomeClose = () => {
        localStorage.setItem('gonkle_has_visited', 'true');
        setShowWelcome(false);
        
        // 1 Sekunde Pause, dann Save Access Overlay anzeigen
        setTimeout(() => {
            setShowSaveAccessOverlay(true);
            
            // 2 Sekunden anzeigen, dann automatisch verschwinden (kürzer wegen besserer Lesbarkeit)
            setTimeout(() => {
                setShowSaveAccessOverlay(false);
            }, 2000);
        }, 1000);
    };

    useEffect(() => {
        if (eventIdFromUrl) {
            const eventFromContext = events.find(e => e.id.toString() === eventIdFromUrl);
            if (eventFromContext) {
                setSelectedEvent(eventFromContext);
                // Keep URL intact
            } else {
                // If not in context, fetch it directly
                api.get(`/api/events/${eventIdFromUrl}`)
                    .then(res => {
                        const fetchedEvent = res.data;
                        if (fetchedEvent) {
                            setSelectedEvent(fetchedEvent);
                            setFlyToEvent(fetchedEvent); // Also fly to it
                        }
                    })
                    .catch(err => {
                        console.error(`Failed to fetch event ${eventIdFromUrl}`, err);
                        showError(t('error_fetching_event', 'Could not load the linked event.'));
                        navigate('/', { replace: true }); // Go back to map if event not found
                    });
            }
        }
    }, [eventIdFromUrl, events, api, setSelectedEvent, setFlyToEvent, navigate, showError, t]);

    // Zeige eventError als Toast
    useEffect(() => {
        if (eventError) {
            showError(eventError);
        }
    }, [eventError, showError]);

    // Handle service worker messages (from push notification clicks)
    useEffect(() => {
        const handleServiceWorkerMessage = (event) => {
            if (event.data?.type === 'OPEN_EVENT' && event.data?.eventId) {
                const eventToOpen = events.find(e => e.id === event.data.eventId);
                if (eventToOpen) {
                    setSelectedEvent(eventToOpen);
                    setFlyToEvent(eventToOpen);
                }
            }
        };

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
            return () => {
                navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
            };
        }
    }, [events]);

    const handleLogout = () => { logout(); };

    const handleFlyToEvent = (event) => {
        setViewMode('all');
        setFlyToEvent(event);
        setShowMyEvents(false);
    };

    const handleViewportChange = useCallback((bounds) => {
        setMapBounds(bounds);
        fetchEventsByBounds(bounds);
    }, [fetchEventsByBounds, setMapBounds]);
    
    const handleVibeChange = async (newVibe) => {
        try {
            await updateUser({ favorite_sport: newVibe });
            setShowChangeVibe(false);
        } catch (error) {
            // Error handled
        }
    };

    const handleNotificationClick = () => {
        if (notification?.eventId) {
            const event = events.find(e => e.id === notification.eventId);
            if (event) {
                setSelectedEvent(event);
                setFlyToEvent(event);
            }
        }
    };

    return (
        <div className="map-main-container">
            <div className="topbar-fixed">
                <div className="topbar-flex">
                    <TopBarButton
                        icon={
                            <img
                                // VORBEREITUNG FÜR CDN: BILD-URLS MÜSSEN ABSOLUT SEIN
                                src={user?.selfie_path ? `${process.env.REACT_APP_IMAGE_BASE_URL || ''}/${user.selfie_path}` : ''}
                                alt="me"
                                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '2px solid #555' }}
                            />
                        }
                        label={t('me_button')}
                        onClick={() => setShowMyEvents(true)}
                        badge={pendingRequestCount}
                        active={showMyEvents}
                    />
                    <TopBarButton
                        icon={viewModeIcon}
                        label={viewModeText}
                        onClick={cycleViewMode}
                        active={false}
                    />
                    <TopBarButton
                        icon={"🌐"}
                        label={t('lang_button')}
                        onClick={() => setShowLanguageModal(true)}
                        active={showLanguageModal}
                    />
                    <TopBarButton
                        icon={<span style={{ position: 'relative' }}>💡</span>}
                        label={t('nearby_button', 'Nearby')}
                        onClick={() => setShowSuggestedGonkles(!showSuggestedGonkles)}
                        badge={suggestedGonkles.length}
                        active={showSuggestedGonkles}
                    />
                    <TopBarButton
                        icon={"⏏️"}
                        label={t('logout_button')}
                        onClick={handleLogout}
                        active={false}
                    />
                </div>
            </div>

            {weather && (
                <div className="weather-box">
                    {weather.temp}°C {weather.description}
                </div>
            )}
            
            <div className="map-inner-container">
                <MapContainer center={defaultPosition} zoom={6} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapController 
                        user={user} 
                        setCreateEventCoords={setCreateEventCoords} 
                        eventsToDisplay={filteredEvents} 
                        setSelectedEvent={setSelectedEvent} 
                        onViewportChange={handleViewportChange} 
                    />
                    <FlyToController event={flyToEvent} />
                </MapContainer>
                
                <div className="status-bar-bottom">
                    <AnimatePresence>
                        {hasVisibleNotification ? (
                            <motion.div
                                key="notification"
                                initial={{ y: "100%", opacity: 0 }}
                                animate={{ y: "0%", opacity: 1 }}
                                exit={{ y: "100%", opacity: 0 }}
                                transition={{ type: 'spring', stiffness: 100, damping: 20 }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    background: 'rgba(99, 102, 241, 0.95)',
                                    padding: '0 15px',
                                    cursor: notification.eventId ? 'pointer' : 'default'
                                }}
                                onClick={notification.eventId ? handleNotificationClick : undefined}
                            >
                                <div className={notification.text.length > 50 ? 'marquee-scroll' : ''} style={{ color: '#fff', fontWeight: '700', textShadow: 'none', whiteSpace: 'nowrap', fontSize: '1em' }}>
                                    {notification.text}
                                </div>
                            </motion.div>
                        ) : (
                            <StatusBarContent />
                        )}
                    </AnimatePresence>
                </div>

                {/* eventError wird jetzt als Toast angezeigt */}
                {/* Notification-Banner Beispiel (falls benötigt):
                <div className="notification-banner">Fehlertext oder Hinweis</div>
                */}
            </div>
            
            {createEventCoords && <EventCreationModal coords={createEventCoords} onClose={() => setCreateEventCoords(null)} />}

            {showSuggestedGonkles && (
                <div className="suggested-gonkles-container">
                    <div className="suggested-gonkles-header">
                        <h3 className="suggested-gonkles-title">
                            💡 Suggested Gonkles Nearby
                        </h3>
                        <button
                            onClick={() => setShowSuggestedGonkles(false)}
                            className="suggested-gonkles-close-btn"
                        >
                            ✕
                        </button>
                    </div>

                    {loadingSuggested ? (
                        <div className="suggested-gonkles-message">
                            Loading suggestions...
                        </div>
                    ) : suggestedGonkles.length > 0 ? (
                        <div className="suggested-gonkles-list">
                            {suggestedGonkles.slice(0,5).map((gonkle, idx) => (
                                <div
                                    key={`${gonkle.id}_${idx}`}
                                    className="suggested-gonkle-item"
                                    onClick={() => { /* In future: navigate to event details */ }}
                                >
                                    <div className="suggested-gonkle-item-title">
                                        {gonkle.title}
                                    </div>
                                    <div className="suggested-gonkle-item-details">
                                        {gonkle.venue_name} · {formatDateTime(gonkle.start_date, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                    <a
                                        href={gonkle.eventbrite_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="suggested-gonkle-item-link"
                                    >
                                        View on Eventbrite →
                                    </a>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="suggested-gonkles-message">
                            No suggestions nearby yet. Check back later!
                        </div>
                    )}
                </div>
            )}

            {showMyEvents && <MyEventsModal
                onClose={() => setShowMyEvents(false)}
                onEventClick={handleFlyToEvent}
                onOpenChangeVibe={handleOpenChangeVibe}
            />}
            {selectedEvent && <EventDetails event={selectedEvent} onClose={handleCloseSelectedEvent} />}
            {showChangeVibe && <ChangeVibeModal onClose={() => setShowChangeVibe(false)} onSave={handleVibeChange} />}

            <AnimatePresence>
                {showWelcome && <WelcomeModal onClose={handleWelcomeClose} />}
                {showLanguageModal && <LanguageModal onClose={() => setShowLanguageModal(false)} />}
                {showSaveAccessOverlay && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 9999,
                            pointerEvents: 'none', // Karte bleibt interaktiv
                            fontSize: 'clamp(3rem, 8vw, 6rem)',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            padding: '20px',
                            boxSizing: 'border-box'
                        }}
                    >
                        <div style={{ 
                            color: '#FFD700', // Gelbe Buchstaben
                            WebkitTextStroke: '3px #000', // Schwarzer Rand
                            textStroke: '3px #000', // Fallback
                            marginBottom: '10px',
                            letterSpacing: '2px'
                        }}>
                            {user?.nickname?.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 12)}-quiche
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <NotificationPrompt />
        </div>
    );
};

export default MapPage;
